import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import {
  Brain, Lightbulb, ChartBar, Compass, Target, Briefcase, Handshake,
  ArrowRight, Hourglass, CheckCircle, ClockClockwise, Lock, Warning, FileText,
} from "@phosphor-icons/react";

// Static catalogue of tests. Only test #1 (breintype) is implemented today;
// the rest are placeholders that the work coach asked us to scaffold.
const TEST_CATALOG = [
  { id: "breintype", title: "Breintype-test",      icon: Brain,     description: "Ontdek hoe jij in elkaar zit met onze breintype-test op basis van het Pad-bij-Aanleg model." },
  { id: "iq",        title: "Klassieke IQ-test",   icon: Lightbulb, description: "Een gestructureerde test die je analytische, logische en verbale vaardigheden meet." },
  { id: "werk",      title: "Werkwaardetest",      icon: ChartBar,  description: "Welke waarden zijn voor jou belangrijk in je werk? Geld, vrijheid, zekerheid, betekenis?" },
  { id: "loopbaan",  title: "Loopbaanwaardetest",  icon: Compass,   description: "Welke richting wil je op in je loopbaan? Welke koers past bij jouw kernwaarden?" },
  { id: "comp",      title: "Competentietest",     icon: Target,    description: "Inzicht in jouw sterkste competenties en welke nog ontwikkeling vragen." },
  { id: "beroep",    title: "Beroepentest",        icon: Briefcase, description: "Welke beroepen passen bij jouw profiel? Een match-analyse op duizenden beroepen." },
  { id: "conflict",  title: "Conflicthanteringstest", icon: Handshake, description: "21 dilemma's op basis van de Cybernetic Big Five-theorie, op maat van jouw breintype en functie." },
];

export default function CoacheeTestsPage() {
  const [progress, setProgress] = useState(null);
  const [iq, setIq] = useState(null);
  const [wv, setWv] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [comp, setComp] = useState(null);
  const [career, setCareer] = useState(null);
  const [prof, setProf] = useState(null);
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        const [pRes, iqRes, wvRes, cRes, compRes, careerRes, profRes, cvRes] = await Promise.allSettled([
          api.get("/survey/progress"),
          own ? api.get(`/coachees/${own.id}/iq-test`) : Promise.resolve({ data: null }),
          own ? api.get(`/coachees/${own.id}/work-values`) : Promise.resolve({ data: null }),
          own ? api.get(`/coachees/${own.id}/conflict-test`) : Promise.resolve({ data: null }),
          own ? api.get(`/coachees/${own.id}/competence`) : Promise.resolve({ data: null }),
          own ? api.get(`/coachees/${own.id}/career-values`) : Promise.resolve({ data: null }),
          own ? api.get(`/coachees/${own.id}/profession-test`) : Promise.resolve({ data: null }),
          own ? api.get(`/coachees/${own.id}/cv`) : Promise.resolve({ data: null }),
        ]);
        if (pRes.status === "fulfilled") setProgress(pRes.value.data);
        if (iqRes.status === "fulfilled") setIq(iqRes.value.data);
        if (wvRes.status === "fulfilled") setWv(wvRes.value.data);
        if (cRes.status === "fulfilled") setConflict(cRes.value.data);
        if (compRes.status === "fulfilled") setComp(compRes.value.data);
        if (careerRes.status === "fulfilled") setCareer(careerRes.value.data);
        if (profRes.status === "fulfilled") setProf(profRes.value.data);
        if (cvRes.status === "fulfilled") setCv(cvRes.value.data);
      } catch (err) {
        console.warn("Tests overview fetch failed:", err?.message || err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Toon de waarschuwing alleen wanneer er nog géén CV-data is. Zodra de
  // coachee minimaal één werkervaring OF een (geïmporteerde) samenvatting +
  // skills heeft staan, gaan we ervan uit dat het CV in orde is.
  const cvData = cv?.cv || {};
  const hasCvContent = !loading && (
    ((cvData.work_experience || []).length > 0)
    || ((cvData.skills || []).length > 0)
    || ((cvData.summary || "").trim().length > 20)
  );
  const showCvWarning = !loading && cv && !hasCvContent;

  return (
    <AppLayout title="Tests">
      <div className="max-w-4xl space-y-6" data-testid="tests-page">
        {showCvWarning && (
          <div
            className="neuro-card-bold p-4 bg-amber-50 border-2 border-amber-300"
            data-testid="cv-first-warning"
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 inline-flex h-10 w-10 items-center justify-center bg-amber-200 text-amber-900 rounded-sm">
                <Warning size={22} weight="duotone" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-base font-bold tracking-tight text-amber-900">
                  Vul eerst je CV in voor het beste resultaat
                </h3>
                <p className="text-sm text-amber-900/90 mt-1 leading-relaxed">
                  Sommige tests — vooral de <strong>Beroepentest</strong>,
                  {" "}<strong>Conflicthanteringstest</strong> en{" "}
                  <strong>Competentietest</strong> — werken veel beter wanneer
                  de AI je werkervaring, functie en branche kent. Je profielen
                  en het LinkedIn-stappenplan worden dan ook veel persoonlijker.
                </p>
                <div className="mt-3">
                  <Link
                    to="/cv"
                    data-testid="cv-first-warning-link"
                    className="inline-flex items-center gap-2 bg-amber-900 text-white px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm hover:bg-amber-800 transition-colors"
                  >
                    <FileText size={14} weight="bold" /> Naar Mijn CV
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
        <p className="text-sm text-zinc-600 max-w-2xl">
          Hieronder vind je alle tests die binnen Werk Dat Past Online beschikbaar zijn. Werk
          ze op je eigen tempo af — je voortgang wordt automatisch bijgehouden.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TEST_CATALOG.map((t) => (
            <TestCard
              key={t.id}
              test={t}
              status={
                t.id === "breintype" ? breintypeStatus(progress, loading)
                : t.id === "iq" ? iqStatus(iq, loading)
                : t.id === "werk" ? workValuesStatus(wv, loading)
                : t.id === "conflict" ? conflictStatus(conflict, loading)
                : t.id === "comp" ? competenceStatus(comp, loading)
                : t.id === "loopbaan" ? careerValuesStatus(career, loading)
                : t.id === "beroep" ? professionStatus(prof, loading)
                : placeholderStatus()
              }
            />
          ))}
        </div>
      </div>
    </AppLayout>
  );
}

function careerValuesStatus(c, loading) {
  if (loading) return { kind: "loading" };
  if (!c) return { kind: "not_started", to: "/tests/loopbaan", action: "Start de test" };
  const total = c.screen_count || 6;
  const done = (c.completed_screens || []).length;
  if (done === 0) {
    return { kind: "not_started", to: "/tests/loopbaan", action: "Start de test" };
  }
  if (done < total) {
    return {
      kind: "in_progress",
      to: "/tests/loopbaan",
      action: "Ga verder",
      label: `${done}/${total} schermen klaar`,
      percent: Math.round((done / total) * 100),
    };
  }
  return {
    kind: "finalized",
    to: "/tests/loopbaan",
    action: "Bekijk je rankings",
    label: "Voltooid",
  };
}

function professionStatus(p, loading) {
  if (loading) return { kind: "loading" };
  if (!p || (p.messages || []).length === 0) {
    return { kind: "not_started", to: "/tests/beroepen", action: "Start de test" };
  }
  if (p.completed || p.completed_at) {
    return {
      kind: "finalized",
      to: "/tests/beroepen",
      action: "Bekijk je rapport",
      label: "Voltooid — rapport beschikbaar",
    };
  }
  const used = p.user_message_count || 0;
  const cap = p.max_user_messages || 40;
  return {
    kind: "in_progress",
    to: "/tests/beroepen",
    action: "Ga verder",
    label: `Bezig — ${used}/${cap} berichten`,
    percent: Math.min(95, Math.max(5, Math.round((used / 20) * 100))),
  };
}

function competenceStatus(c, loading) {
  if (loading) return { kind: "loading" };
  if (!c || (c.competencies || []).length === 0) {
    return { kind: "not_started", to: "/tests/competentie", action: "Start de test" };
  }
  const total = c.competencies.length;
  const scored = Object.keys(c.self_scores || {}).filter(
    (n) => c.competencies.some((x) => x.name === n),
  ).length;
  if (scored < total) {
    return {
      kind: "in_progress",
      to: "/tests/competentie",
      action: "Ga verder",
      label: `${scored}/${total} competenties gescoord`,
      percent: Math.round((scored / total) * 100),
    };
  }
  return {
    kind: "finalized",
    to: "/tests/competentie",
    action: "Bekijk je rapport",
    label: `Voltooid · ${c.feedback_count || 0} 360°-reacties`,
  };
}

function conflictStatus(c, loading) {
  if (loading) return { kind: "loading" };
  if (!c) return { kind: "not_started", to: "/tests/conflict", action: "Start de test" };
  if (c.available === false) {
    return {
      kind: "disabled",
      label: "Eerst breintype definitief",
    };
  }
  const used = c.user_message_count || 0;
  const cap = c.max_user_messages || 50;
  const msgCount = (c.messages || []).length;
  if (msgCount === 0) {
    return { kind: "not_started", to: "/tests/conflict", action: "Start de test" };
  }
  // Once the AI has generated the final phase-4 report, the test is over —
  // the coachee can only re-read the report from now on. Don't fall back on
  // the message cap, because the report typically arrives well before 50.
  if (c.completed === true || c.completed_at) {
    return {
      kind: "finalized",
      to: "/tests/conflict",
      action: "Bekijk je rapport",
      label: "Voltooid — rapport beschikbaar",
    };
  }
  if (c.limit_reached || used >= cap) {
    return {
      kind: "finalized",
      to: "/tests/conflict",
      action: "Bekijk je gesprek",
      label: "Berichtenlimiet bereikt",
    };
  }
  return {
    kind: "in_progress",
    to: "/tests/conflict",
    action: "Ga verder met de test",
    label: `Bezig — ${used} berichten verstuurd`,
    percent: Math.min(95, Math.max(5, Math.round((used / 24) * 100))),
  };
}

function workValuesStatus(wv, loading) {
  if (loading) return { kind: "loading" };
  if (!wv || wv.state === "not_started") return { kind: "not_started", to: "/tests/werkwaarde", action: "Start de test" };
  if (wv.state === "completed" && wv.result) {
    return {
      kind: "finalized",
      to: "/tests/werkwaarde",
      action: "Bekijk je top 10",
      label: `Top 1: ${wv.result[0]}`,
    };
  }
  if (wv.state === "selection") {
    return {
      kind: "in_progress",
      to: "/tests/werkwaarde",
      action: "Ga verder",
      label: "Bezig — werkwaarden selecteren",
      percent: 5,
    };
  }
  if (wv.state === "comparing") {
    const idx = wv.pair_index || 0;
    const total = wv.pairs_total || 45;
    return {
      kind: "in_progress",
      to: "/tests/werkwaarde",
      action: "Hervat de test",
      label: `Bezig — vergelijking ${idx + 1} van ${total}`,
      percent: Math.max(10, Math.round((idx / total) * 100)),
    };
  }
  return { kind: "not_started", to: "/tests/werkwaarde", action: "Start de test" };
}

function iqStatus(iq, loading) {
  if (loading) return { kind: "loading" };
  if (!iq) return { kind: "not_started", to: "/tests/iq", action: "Start de test" };
  if (iq.state === "completed" && iq.result) {
    return {
      kind: "finalized",
      to: "/tests/iq",
      action: "Bekijk je resultaat",
      label: `IQ ${Math.round(iq.result.iq_mean)} (95% BI ${iq.result.iq_ci_low}–${iq.result.iq_ci_high})`,
    };
  }
  if (iq.state === "in_progress") {
    const ans = Object.keys(iq.answers || {}).length;
    const m = Math.floor((iq.remaining_seconds || 0) / 60);
    return {
      kind: "in_progress",
      to: "/tests/iq",
      action: "Hervat de test",
      label: `Bezig — ${ans} van ${iq.total_items} (nog ${m} min)`,
      percent: Math.round((ans / iq.total_items) * 100),
    };
  }
  return { kind: "not_started", to: "/tests/iq", action: "Start de test" };
}

function breintypeStatus(progress, loading) {
  if (loading) return { kind: "loading" };
  if (!progress) return { kind: "not_started", to: "/survey", action: "Start de test" };
  const { state, answered_count: ans, total } = progress;
  switch (state) {
    case "disabled":
      return { kind: "disabled", label: "Niet beschikbaar" };
    case "not_started":
      return { kind: "not_started", to: "/survey", action: "Start de test" };
    case "in_progress":
      return {
        kind: "in_progress",
        to: "/survey",
        action: "Hervat de test",
        label: `Bezig — ${ans} van ${total} vragen`,
        percent: Math.round((ans / total) * 100),
      };
    case "awaiting_validation":
      return {
        kind: "awaiting_validation",
        to: "/profielen/breintype",
        action: "Bekijk voorlopige uitslag",
        label: "Te valideren door work coach",
      };
    case "finalized":
      return {
        kind: "finalized",
        to: "/profielen/breintype",
        action: "Bekijk je breintype",
        label: "Definitief bepaald",
      };
    default:
      return { kind: "not_started", to: "/survey", action: "Start de test" };
  }
}

function placeholderStatus() {
  return { kind: "placeholder", label: "Binnenkort beschikbaar" };
}

function TestCard({ test, status }) {
  const Icon = test.icon;
  const isPlaceholder = status.kind === "placeholder" || status.kind === "disabled";
  return (
    <div
      className={
        "neuro-card-bold p-5 bg-white flex flex-col gap-4 transition-colors " +
        (isPlaceholder ? "opacity-70" : "hover:border-zinc-900")
      }
      data-testid={`test-card-${test.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex h-11 w-11 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm">
          <Icon size={22} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg font-bold tracking-tight">{test.title}</h3>
          <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{test.description}</p>
        </div>
      </div>
      <StatusFooter status={status} testId={test.id} />
    </div>
  );
}

function StatusFooter({ status, testId }) {
  if (status.kind === "loading") {
    return <div className="text-xs text-zinc-400 uppercase tracking-widest">Laden...</div>;
  }
  if (status.kind === "placeholder") {
    return (
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500" data-testid={`test-status-${testId}`}>
        <Hourglass size={14} weight="duotone" /> {status.label}
      </div>
    );
  }
  if (status.kind === "disabled") {
    return (
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400" data-testid={`test-status-${testId}`}>
        <Lock size={14} weight="duotone" /> {status.label}
      </div>
    );
  }
  if (status.kind === "in_progress") {
    return (
      <div className="space-y-2" data-testid={`test-status-${testId}`}>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#1E88E5]">
          <ClockClockwise size={14} weight="duotone" /> {status.label}
        </div>
        <div className="h-1 bg-zinc-100 rounded-sm overflow-hidden">
          <div className="h-full bg-[#1E88E5] transition-all" style={{ width: `${status.percent}%` }} />
        </div>
        <CardAction to={status.to} label={status.action} testId={testId} />
      </div>
    );
  }
  if (status.kind === "awaiting_validation") {
    return (
      <div className="space-y-2" data-testid={`test-status-${testId}`}>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-600">
          <ClockClockwise size={14} weight="duotone" /> {status.label}
        </div>
        <CardAction to={status.to} label={status.action} testId={testId} />
      </div>
    );
  }
  if (status.kind === "finalized") {
    return (
      <div className="space-y-2" data-testid={`test-status-${testId}`}>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#43A047]">
          <CheckCircle size={14} weight="bold" /> {status.label}
        </div>
        <CardAction to={status.to} label={status.action} testId={testId} />
      </div>
    );
  }
  // not_started
  return (
    <div className="space-y-2" data-testid={`test-status-${testId}`}>
      <div className="text-xs uppercase tracking-widest text-zinc-500">Nog niet gestart</div>
      <CardAction to={status.to} label={status.action} testId={testId} />
    </div>
  );
}

function CardAction({ to, label, testId }) {
  return (
    <Link
      to={to}
      data-testid={`test-action-${testId}`}
      className="inline-flex items-center gap-1.5 text-sm font-semibold tracking-wide uppercase text-zinc-900 hover:text-[#1E88E5] transition-colors"
    >
      {label} <ArrowRight size={14} />
    </Link>
  );
}
